'use client';

import { useDialogStore } from '@/app/lib/stores/dialog.store';
import { clsxMerge } from '@/app/lib/utils/styles';
import CloseIcon from '@/app/ui/assets/icons/close-icon';
import SearchIcon from '@/app/ui/assets/icons/search-icon';
import useKeyboardShortcut from '@/app/ui/hooks/use-keyboard-shortcut';
import { Input } from 'tailframes-components/components/input';
import { navSections, NavSectionType } from '@/app/ui/navigation/config';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';

export default function SearchDialogContent() {
  const { setIsDialogVisible } = useDialogStore();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const items = navSections
    .filter(s => s.type !== NavSectionType.GettingStarted)
    .flatMap(s => s.items.map(i => ({ ...i, section: s.label, baseHref: s.href, icon: s.icon })));

  const foundItems = items.filter(i => i.name.toLowerCase().includes(value.toLowerCase()));

  const handleClose = useCallback(() => {
    setIsDialogVisible(false);
    setTimeout(() => {
      setValue('');
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useKeyboardShortcut(handleClose, { code: 'Escape', desktopOnly: true });

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 200);
  }, []);

  return (
    <div className='w-screen bg-transparent md:h-[50vh] md:w-[500px]'>
      <div className='z-10 w-full overflow-hidden bg-white md:h-auto md:rounded-lg md:shadow'>
        <Input
          ref={inputRef}
          id='search'
          size='large'
          placeholder='Search anything'
          className='h-14 border-none pl-12 pr-10 focus:outline-none focus:ring-0 focus:ring-offset-0'
          containerClassName='w-full max-w-full'
          startAdornment={<SearchIcon />}
          endAdornment={<CloseIcon className='size-4 cursor-pointer stroke-inherit hover:opacity-80' onClick={handleClose} />}
          value={value}
          onChange={e => {
            setValue(e.target.value);
          }}
        />
        <ul className='flex h-[calc(100vh-3.5rem)] flex-col overflow-auto border border-t-2 p-2 md:h-auto md:max-h-[300px]'>
          <li
            className={clsxMerge('text-center text-sm leading-[44px] text-gray-500 transition-[opacity] duration-[300ms] ease-linear', {
              'max-h-0 opacity-0': foundItems.length > 0,
            })}
          >
            No results found
          </li>
          {foundItems.map(({ icon: Icon, ...i }) => (
            <li key={i.href}>
              <Link
                href={`${i.baseHref}${i.href}`}
                className='flex h-11 w-full cursor-pointer items-center justify-between rounded-lg px-2 py-3 hover:bg-gray-100'
                onClick={handleClose}
              >
                <div className='flex items-center gap-2'>
                  {Icon && <Icon />}
                  <span className='text-sm font-semibold text-black'>{i.name}</span>
                </div>
                <span className='right-2 whitespace-nowrap text-xs'>{i.section}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
